import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { PostgrestSingleResponse } from '@supabase/supabase-js';
import type { Tables } from '@/integrations/supabase/types';
import { DEFAULT_LOCALE, type Locale } from './useLanguage';

export type DBCategory = Tables<'categories'>;
export type DBProductType = {
  id: string;
  product_id: string;
  label: string;
  emoji: string | null;
  smm_panel_id: string | null;
  smm_service_id: string | null;
  sort_order: number;
  created_at: string;
};
export type DBProductGift = {
  id: string;
  product_id: string;
  label: string;
  quantity: number;
  smm_panel_id: string | null;
  smm_service_id: string | null;
  input_label: string | null;
  input_placeholder: string | null;
  sort_order: number;
  created_at: string;
};
export type DBProduct = Tables<'products'> & {
  product_variations: Tables<'product_variations'>[];
  product_types?: DBProductType[];
  product_gifts?: DBProductGift[];
};

const QUERY_TIMEOUT_MS = 3000;
const MAX_RETRIES = 1;
const CACHE_TTL_MS = 10 * 60 * 1000;
const FALLBACK_RETRY_MS = 2500;

type CacheSource = 'db' | 'fallback';

type CachedValue<T> = {
  value: T;
  ts: number;
  source: CacheSource;
};

let categoriesCache: CachedValue<DBCategory[]> | null = null;
let productsCache: CachedValue<DBProduct[]> | null = null;
const categoryBySlugCache = new Map<string, CachedValue<DBCategory | null>>();
const productBySlugCache = new Map<string, CachedValue<DBProduct | null>>();
const productByLocaleSlugCache = new Map<string, CachedValue<DBProduct | null>>();

let categoriesInFlight: Promise<DBCategory[]> | null = null;
let productsInFlight: Promise<DBProduct[]> | null = null;

function getCacheTtl(source: CacheSource) {
  return source === 'fallback' ? FALLBACK_RETRY_MS : CACHE_TTL_MS;
}

function isCacheValid(entry: CachedValue<unknown> | null | undefined) {
  if (!entry) return false;
  return Date.now() - entry.ts < getCacheTtl(entry.source);
}

function createCacheEntry<T>(value: T, source: CacheSource): CachedValue<T> {
  return { value, source, ts: Date.now() };
}

function readCacheEntry<T>(entry: CachedValue<T> | null | undefined): CachedValue<T> | null {
  return isCacheValid(entry) ? entry! : null;
}

function readCache<T>(entry: CachedValue<T> | null | undefined): T | null {
  return isCacheValid(entry) ? entry!.value : null;
}

function writeMapCache<T>(cache: Map<string, CachedValue<T>>, key: string, value: T, source: CacheSource) {
  cache.set(key, createCacheEntry(value, source));
}

function readMapCache<T>(cache: Map<string, CachedValue<T>>, key: string): T | undefined {
  const entry = cache.get(key);
  if (!entry) return undefined;
  if (!isCacheValid(entry)) {
    cache.delete(key);
    return undefined;
  }
  return entry.value;
}

function readMapCacheEntry<T>(cache: Map<string, CachedValue<T>>, key: string): CachedValue<T> | null {
  const entry = cache.get(key);
  if (!entry) return null;
  if (!isCacheValid(entry)) {
    cache.delete(key);
    return null;
  }
  return entry;
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

function warmCategoryCache(categories: DBCategory[], source: CacheSource) {
  categories.forEach(category => writeMapCache(categoryBySlugCache, category.slug, category, source));
}

function warmProductCache(products: DBProduct[], source: CacheSource) {
  products.forEach(product => writeMapCache(productBySlugCache, product.slug, product, source));
}

async function withTimeout<T>(
  promiseLike: PromiseLike<PostgrestSingleResponse<T>>,
  label: string
): Promise<PostgrestSingleResponse<T>> {
  const promise = Promise.resolve(promiseLike);

  return new Promise<PostgrestSingleResponse<T>>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error(`${label} timed out after ${QUERY_TIMEOUT_MS}ms`));
    }, QUERY_TIMEOUT_MS);

    promise
      .then(result => {
        window.clearTimeout(timeoutId);
        resolve(result);
      })
      .catch(error => {
        window.clearTimeout(timeoutId);
        reject(error);
      });
  });
}

async function fetchWithRetry<T>(
  fn: () => PromiseLike<PostgrestSingleResponse<T>>,
  label: string
): Promise<PostgrestSingleResponse<T>> {
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      return await withTimeout(fn(), label);
    } catch (err) {
      console.warn(`[${label}] Attempt ${attempt + 1} failed:`, err);
      if (attempt === MAX_RETRIES) throw err;
      await sleep(1000 * (attempt + 1));
    }
  }

  throw new Error(`${label} failed after ${MAX_RETRIES + 1} attempts`);
}

function loadCategoriesOnce(): Promise<DBCategory[]> {
  const cached = readCacheEntry(categoriesCache);
  if (cached?.source === 'db') return Promise.resolve(cached.value);
  if (categoriesInFlight) return categoriesInFlight;

  categoriesInFlight = (async () => {
    try {
      const { data, error } = await fetchWithRetry<DBCategory[]>(
        () => supabase.from('categories').select('*').order('sort_order'),
        'useCategories'
      );
      if (error) throw error;

      // Only cache real DB results. If the DB returns an empty array we
      // intentionally do NOT poison the cache with the static fallback –
      // otherwise old bundled data (wrong images, missing locale variants)
      // would flash for several seconds on every mount.
      if (data && data.length > 0) {
        categoriesCache = createCacheEntry(data, 'db');
        warmCategoryCache(data, 'db');
        return data;
      }
      // Empty DB result – throw so caller retries instead of showing fallback.
      throw new Error('useCategories: empty result');
    } catch (err) {
      console.warn('[useCategories] Fetch failed:', err);
      // Do NOT cache failures. Caller will retry on next call.
      throw err;
    } finally {
      categoriesInFlight = null;
    }
  })();

  return categoriesInFlight;
}

function loadProductsOnce(): Promise<DBProduct[]> {
  const cached = readCacheEntry(productsCache);
  if (cached?.source === 'db') return Promise.resolve(cached.value);
  if (productsInFlight) return productsInFlight;

  productsInFlight = (async () => {
    try {
      const { data, error } = await fetchWithRetry<DBProduct[]>(
        () =>
          supabase
            .from('products')
            .select('*, product_variations(*), product_types(*), product_gifts!product_gifts_product_id_fkey(*)')
            .eq('is_active', true)
            .order('sort_order'),
        'useAllProducts'
      );
      if (error) throw error;

      if (data && data.length > 0) {
        productsCache = createCacheEntry(data, 'db');
        warmProductCache(data, 'db');
        return data;
      }
      throw new Error('useAllProducts: empty result');
    } catch (err) {
      console.warn('[useAllProducts] Fetch failed:', err);
      throw err;
    } finally {
      productsInFlight = null;
    }
  })();

  return productsInFlight;
}

export function useCategories() {
  const seed = categoriesCache?.source === 'db' ? (readCache(categoriesCache) ?? []) : [];
  const [categories, setCategories] = useState<DBCategory[]>(seed);
  const [loading, setLoading] = useState(seed.length === 0);

  useEffect(() => {
    let active = true;
    let retryTimer: number | null = null;


    const run = (attempt: number) => {
      loadCategoriesOnce()
        .then(resolved => {
          if (!active) return;
          setCategories(resolved);
          setLoading(false);
        })
        .catch((error) => {
          if (!active) return;
          // Retry with backoff, keep skeleton until DB responds.
          const wait = Math.min(800 + attempt * 400, 3000);
          retryTimer = window.setTimeout(() => run(attempt + 1), wait);
        });
    };

    if (seed.length === 0) {
      run(0);
    } else {
    }

    return () => {
      active = false;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { categories, loading };
}

export function useProductsByCategory(categoryId: string | undefined) {
  const [products, setProducts] = useState<DBProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    let settled = false;


    if (!categoryId) {
      setProducts([]);
      setLoading(false);
      return;
    }

    const cachedProducts = readCache(productsCache);
    if (cachedProducts) {
      const filtered = cachedProducts.filter(product => product.category_id === categoryId);
      if (filtered.length > 0 || productsCache?.source === 'db') {
        setProducts(filtered);
        setLoading(false);
        return;
      }
    }

    setLoading(true);

    let retryTimer: number | null = null;

    const fetchOnce = async (attempt: number) => {
      try {
        const { data, error } = await fetchWithRetry<DBProduct[]>(
          () =>
            supabase
              .from('products')
              .select('*, product_variations(*), product_types(*), product_gifts!product_gifts_product_id_fkey(*)')
              .eq('category_id', categoryId)
              .eq('is_active', true)
              .order('sort_order'),
          'useProductsByCategory'
        );

        if (error) throw error;
        if (!active || settled) return;
        settled = true;
        setProducts(data ?? []);
        setLoading(false);
      } catch (err) {
        console.warn('[useProductsByCategory] Fetch failed, retrying:', err);
        if (!active || settled) return;
        const wait = Math.min(800 + attempt * 400, 3000);
        retryTimer = window.setTimeout(() => fetchOnce(attempt + 1), wait);
      }
    };

    fetchOnce(0);

    return () => {
      active = false;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [categoryId]);

  return { products, loading };
}

export function useAllProducts() {
  const seed = productsCache?.source === 'db' ? (readCache(productsCache) ?? []) : [];
  const [products, setProducts] = useState<DBProduct[]>(seed);
  const [loading, setLoading] = useState(seed.length === 0);

  useEffect(() => {
    let active = true;
    let retryTimer: number | null = null;


    const run = (attempt: number) => {
      loadProductsOnce()
        .then(resolved => {
          if (!active) return;
          setProducts(resolved);
          setLoading(false);
        })
        .catch((error) => {
          if (!active) return;
          const wait = Math.min(800 + attempt * 400, 3000);
          retryTimer = window.setTimeout(() => run(attempt + 1), wait);
        });
    };

    if (seed.length === 0) {
      run(0);
    } else {
    }

    return () => {
      active = false;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { products, loading };
}

export function useProductBySlug(slug: string | undefined, locale: Locale = DEFAULT_LOCALE) {
  const [product, setProduct] = useState<DBProduct | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    let settled = false;


    if (!slug) {
      setProduct(null);
      setLoading(false);
      return;
    }

    const localeCacheKey = `${locale}:${slug}`;
    const localeCachedEntry = readMapCacheEntry(productByLocaleSlugCache, localeCacheKey);
    if (localeCachedEntry?.source === 'db') {
      setProduct(localeCachedEntry.value);
      setLoading(false);
      return;
    }

    const cachedEntry = readMapCacheEntry(productBySlugCache, slug);
    if (cachedEntry?.source === 'db') {
      setProduct(cachedEntry.value);
      setLoading(false);
      return;
    }

    const cachedProductsEntry = readCacheEntry(productsCache);
    const cachedFromProducts = cachedProductsEntry?.source === 'db'
      ? cachedProductsEntry.value.find(item => item.slug === slug) ?? null
      : null;
    if (cachedFromProducts) {
      const source = cachedProductsEntry?.source ?? 'db';
      writeMapCache(productBySlugCache, slug, cachedFromProducts, source);
      writeMapCache(productByLocaleSlugCache, localeCacheKey, cachedFromProducts, source);
      setProduct(cachedFromProducts);
      setLoading(false);
      return;
    }

    setLoading(true);

    let retryTimer: number | null = null;

    const fetchOnce = async (attempt: number) => {
      try {
        const { data, error } = await fetchWithRetry<DBProduct | null>(
          () =>
            supabase
              .from('products')
              .select('*, product_variations(*), product_types(*), product_gifts!product_gifts_product_id_fkey(*)')
              .eq('slug', slug)
              .eq('is_active', true)
              .maybeSingle(),
          'useProductBySlug'
        );

        if (error) throw error;

        let resolved = data;

        if (!resolved && locale !== DEFAULT_LOCALE) {
          const { data: translatedMatch, error: translatedMatchError } = await fetchWithRetry<{ product_id: string } | null>(
            () =>
              supabase
                .from('product_translations')
                .select('product_id')
                .eq('locale', locale)
                .eq('slug', slug)
                .maybeSingle(),
            'useProductBySlug.translationMatch'
          );

          if (translatedMatchError) throw translatedMatchError;

          if (translatedMatch?.product_id) {
            const { data: translatedProduct, error: translatedProductError } = await fetchWithRetry<DBProduct | null>(
              () =>
                supabase
                  .from('products')
                  .select('*, product_variations(*), product_types(*), product_gifts!product_gifts_product_id_fkey(*)')
                  .eq('id', translatedMatch.product_id)
                  .eq('is_active', true)
                  .maybeSingle(),
              'useProductBySlug.translationProduct'
            );

            if (translatedProductError) throw translatedProductError;
            resolved = translatedProduct;
          }
        }

        if (!active || settled) return;
        settled = true;
        if (resolved) {
          writeMapCache(productBySlugCache, slug, resolved, 'db');
          writeMapCache(productByLocaleSlugCache, localeCacheKey, resolved, 'db');
          writeMapCache(productBySlugCache, resolved.slug, resolved, 'db');
          writeMapCache(productByLocaleSlugCache, `${DEFAULT_LOCALE}:${resolved.slug}`, resolved, 'db');
        }
        setProduct(resolved ?? null);
        setLoading(false);
      } catch (err) {
        console.warn('[useProductBySlug] Fetch failed, retrying:', err);
        if (!active || settled) return;
        const wait = Math.min(800 + attempt * 400, 3000);
        retryTimer = window.setTimeout(() => fetchOnce(attempt + 1), wait);
      }
    };

    fetchOnce(0);

    return () => {
      active = false;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [slug, locale]);

  return { product, loading };
}

export function useCategoryBySlug(slug: string | undefined) {
  const initialCached = slug
    ? (readMapCacheEntry(categoryBySlugCache, slug)?.source === 'db'
        ? readMapCacheEntry(categoryBySlugCache, slug)?.value
        : readCacheEntry(categoriesCache)?.source === 'db'
          ? readCacheEntry(categoriesCache)?.value.find(category => category.slug === slug) ?? null
          : null)
    : null;
  const [category, setCategory] = useState<DBCategory | null>(initialCached);
  const [loading, setLoading] = useState(initialCached === null);

  useEffect(() => {
    let active = true;
    let settled = false;


    if (!slug) {
      setCategory(null);
      setLoading(false);
      return;
    }

    const cachedEntry = readMapCacheEntry(categoryBySlugCache, slug);
    if (cachedEntry?.source === 'db') {
      setCategory(cachedEntry.value);
      setLoading(false);
      return;
    }

    const cachedCategoriesEntry = readCacheEntry(categoriesCache);
    const cachedFromCategories = cachedCategoriesEntry?.source === 'db'
      ? cachedCategoriesEntry.value.find(item => item.slug === slug) ?? null
      : null;
    if (cachedFromCategories) {
      const source = cachedCategoriesEntry?.source ?? 'db';
      writeMapCache(categoryBySlugCache, slug, cachedFromCategories, source);
      setCategory(cachedFromCategories);
      setLoading(false);
      return;
    }

    setLoading(true);

    let retryTimer: number | null = null;

    const fetchOnce = async (attempt: number) => {
      try {
        const { data, error } = await fetchWithRetry<DBCategory | null>(
          () => supabase.from('categories').select('*').eq('slug', slug).maybeSingle(),
          'useCategoryBySlug'
        );

        if (error) throw error;

        if (!active || settled) return;
        settled = true;
        if (data) {
          writeMapCache(categoryBySlugCache, slug, data, 'db');
        }
        setCategory(data ?? null);
        setLoading(false);
      } catch (err) {
        console.warn('[useCategoryBySlug] Fetch failed, retrying:', err);
        if (!active || settled) return;
        const wait = Math.min(800 + attempt * 400, 3000);
        retryTimer = window.setTimeout(() => fetchOnce(attempt + 1), wait);
      }
    };

    fetchOnce(0);

    return () => {
      active = false;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [slug]);

  return { category, loading };
}
